import type { ReactNode } from 'react';
import type { FiltersApplied, FvAppliedFilters, ResultSummary } from '@/types';

function enabledText(on: boolean | undefined): string {
  return on ? 'Enabled' : 'Disabled';
}

function pickBool(filters: FiltersApplied | undefined, ...keys: (keyof FiltersApplied)[]): boolean {
  for (const key of keys) {
    const v = filters?.[key];
    if (typeof v === 'boolean') return v;
  }
  return false;
}

function locationText(filters: FiltersApplied | undefined): ReactNode {
  const groups = filters?.locationGroups;
  if (!groups || groups.length === 0) {
    const loc = filters?.location;
    const parts = [loc?.state, loc?.country].filter(Boolean);
    return parts.length ? parts.join(', ') : 'Not specified';
  }
  return groups.map((group, i) => {
    const parts = [group.continent, group.country, group.state, group.city].filter(Boolean);
    return (
      <span key={i}>
        {i + 1}: [ {parts.join(', ')} ]{i < groups.length - 1 ? <br /> : null}
      </span>
    );
  });
}

function FilterCell({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rep-pr-filter-cell">
      <span className="rep-pr-filter-cell__label">{label}</span>
      <span className="rep-pr-filter-cell__value">{value || 'Not specified'}</span>
    </div>
  );
}

function rangeText(min: unknown, max: unknown): string {
  if (min == null && max == null) return 'Not specified';
  const a = min == null || min === '' ? 'Any' : String(min);
  const b = max == null || max === '' ? 'Any' : String(max);
  if (a === 'Any' && b === 'Any') return 'Not specified';
  return `${a} - ${b}`;
}

export default function PreviousResultExpandPanel({
  result,
  kind,
}: {
  result: ResultSummary;
  kind: 'research' | 'fv';
}) {
  const files = result.filesUploaded ?? [];
  const inputRecords = files.reduce((sum, f) => sum + (f.totalFileRecords ?? 0), 0);
  const matches = (kind === 'fv' ? result.totalCount : result.totalMatches) ?? 0;
  const totalRecords = result.totalRows ?? (inputRecords > 0 ? inputRecords : matches);

  const stats: { label: string; value: number }[] =
    kind === 'fv'
      ? [{ label: 'Total Firms', value: matches }]
      : [
          { label: 'Total Files Processed', value: files.length },
          { label: 'Input Records Processed (Files)', value: inputRecords },
          { label: 'Total Records Processed', value: totalRecords },
          { label: 'Total Matches', value: matches },
        ];

  return (
    <div className="rep-pr-expand">
      <h3 className="rep-pr-expand__title">
        {kind === 'fv' ? 'Run summary' : 'Files Processing Summary'}
      </h3>
      <div
        className={`rep-pr-expand__stats${kind === 'fv' ? ' rep-pr-expand__stats--compact' : ''}`}
      >
        {stats.map((s) => (
          <div key={s.label} className="rep-pr-expand__stat">
            <p className="rep-pr-expand__stat-value">{s.value.toLocaleString()}</p>
            <p className="rep-pr-expand__stat-label">{s.label}</p>
          </div>
        ))}
      </div>

      <h3 className="rep-pr-expand__title">Applied Filters</h3>
      {kind === 'fv' ? (
        <FvFilterGrid filters={result.filtersApplied} />
      ) : (
        <ResearchFilterGrid filters={result.filtersApplied} />
      )}
    </div>
  );
}

function ResearchFilterGrid({ filters }: { filters: FiltersApplied }) {
  const industryText =
    filters.industryPairs && filters.industryPairs.length > 0
      ? filters.industryPairs
          .map((p) => (p.subIndustry ? `${p.industry} / ${p.subIndustry}` : p.industry))
          .join('; ')
      : 'Not specified';

  const businessQueryText =
    filters.businessQueries && filters.businessQueries.length > 0
      ? filters.businessQueries.join(', ')
      : 'Not specified';

  const customInsightsText =
    filters.customInsights?.questions && filters.customInsights.questions.length > 0
      ? filters.customInsights.questions.map((q, i) => (
          <span key={i}>
            {i + 1}. {q}
            {i < filters.customInsights!.questions.length - 1 ? <br /> : null}
          </span>
        ))
      : 'Not specified';

  const sizeLogic = filters.sizeCriteriaLogic?.trim() || filters.sizeMatchLogic?.trim() || 'N/A';
  const acquisitionNews = pickBool(filters, 'useAcquisitionNews') ?? pickBool(filters, 'useNews');

  return (
    <>
      <div className="rep-pr-expand__filters rep-pr-expand__filters--lead">
        <FilterCell label="Business Query" value={businessQueryText} />
        <FilterCell label="Industry" value={industryText} />
      </div>
      <div className="rep-pr-expand__filters">
        <FilterCell label="Primary Activity" value={filters.primaryActivity ?? 'Not specified'} />
        <FilterCell
          label="Secondary Activity"
          value={filters.secondaryActivity ?? 'Not specified'}
        />
        <FilterCell
          label="Employees"
          value={rangeText(filters.minEmployees, filters.maxEmployees)}
        />
        <FilterCell
          label="Revenue ($M)"
          value={rangeText(filters.minRevenue, filters.maxRevenue)}
        />
        <FilterCell label="Size Match Logic" value={sizeLogic} />
        <FilterCell label="Location" value={locationText(filters)} />
        <FilterCell label="Apollo Enrichment" value={enabledText(pickBool(filters, 'useApollo'))} />
        <FilterCell label="News Enrichment" value={enabledText(pickBool(filters, 'useNews'))} />
        <FilterCell label="Acquisition News" value={enabledText(acquisitionNews)} />
        <FilterCell
          label="LinkedIn Enrichment"
          value={enabledText(pickBool(filters, 'enableLinkedinEnrichment'))}
        />
        <FilterCell
          label="Ownership Enrichment"
          value={enabledText(pickBool(filters, 'ownershipEnrichment'))}
        />
        <FilterCell
          label="Blank-field Web Backfill"
          value={enabledText(pickBool(filters, 'blankFieldBackfill'))}
        />
        <FilterCell
          label="Acquisition Enrichment"
          value={enabledText(pickBool(filters, 'acquisitionEnrichment'))}
        />
        <FilterCell label="Custom Insights" value={customInsightsText} />
      </div>
    </>
  );
}

/** upgrade-5 Unit A — one of the target's own figures, e.g. "$42M"; '' when not given. */
function targetMoney(v?: number | null): string {
  return v == null ? '' : `$${v}M`;
}

function moneyRange(min?: unknown, max?: unknown): string {
  if (min == null && max == null) return '';
  const a = min == null || min === '' ? 'Any' : `$${min}M`;
  const b = max == null || max === '' ? 'Any' : `$${max}M`;
  if (a === 'Any' && b === 'Any') return '';
  return `${a} - ${b}`;
}

function FvFilterGrid({ filters }: { filters: FiltersApplied }) {
  const fv = filters as FiltersApplied & FvAppliedFilters;
  const location = [fv.location?.continent, fv.location?.country, fv.location?.state]
    .filter(Boolean)
    .join(', ');
  const exposure = [
    fv.exposure?.currentPortfolio && 'Current Portfolio',
    fv.exposure?.pastPortfolio && 'Past Portfolio',
    fv.exposure?.listedInterest && 'Listed Interest',
  ].filter(Boolean) as string[];
  const floors = fv.platformFloors;
  const platformSize = floors
    ? [
        floors.ebitda != null && `$${floors.ebitda}M EBITDA`,
        floors.revenue != null && `$${floors.revenue}M revenue`,
      ]
        .filter(Boolean)
        .join(' / ')
    : '';
  const size = fv.size;

  return (
    <div className="rep-pr-expand__filters">
      <FilterCell label="Target Description" value={fv.targetDescription || ''} />
      <FilterCell label="Business Type" value={fv.businessType || ''} />
      <FilterCell label="Industry" value={fv.industry || ''} />
      <FilterCell label="Sub-Industry" value={fv.subIndustry || ''} />
      <FilterCell label="Target Revenue" value={targetMoney(fv.targetFinancials?.revenue)} />
      <FilterCell label="Target EBITDA" value={targetMoney(fv.targetFinancials?.ebitda)} />
      <FilterCell label="Location" value={location} />
      <FilterCell label="Revenue Range" value={moneyRange(size?.revenueMin, size?.revenueMax)} />
      <FilterCell label="EBITDA Range" value={moneyRange(size?.ebitdaMin, size?.ebitdaMax)} />
      <FilterCell
        label="Equity Check"
        value={moneyRange(size?.equityCheckMin, size?.equityCheckMax)}
      />
      <FilterCell
        label="Enterprise Value"
        value={moneyRange(size?.enterpriseValueMin, size?.enterpriseValueMax)}
      />
      {/* Only runs saved while the platform threshold was on carry floors (upgrade-5 Unit B). */}
      {platformSize && <FilterCell label="Platform Size" value={platformSize} />}
      <FilterCell label="PE Exposure" value={exposure.length > 0 ? exposure.join(', ') : ''} />
    </div>
  );
}
